import {
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Button
} from '@mui/material'
import { Mic, StopCircle } from '@mui/icons-material'
import { useEffect, useRef, useState, useId } from 'react'
import SpeechRecognition, {
  useSpeechRecognition
} from 'react-speech-recognition'
import { useToast } from 'services/context/toast'

// Ensures pre-warm runs at most once per page session across all instances
let warmupDone = false

interface TranscriptionInputProps {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  rows?: number
  label?: string
  disabled?: boolean
}

/**
 * TranscriptionInput component
 * TextField with microphone button for voice transcription
 */
export const TranscriptionInput = ({
  value,
  onChange,
  placeholder = 'Digite o texto...',
  rows = 4,
  label,
  disabled = false
}: TranscriptionInputProps) => {
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition()

  const toast = useToast()
  const componentId = useId()
  const [isActiveInput, setIsActiveInput] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [baseText, setBaseText] = useState('')
  const recognitionRef = useRef<any>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const hasStartedListeningRef = useRef(false)

  // Focus input when recording starts
  useEffect(() => {
    if (isActiveInput && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isActiveInput])

  // Configure event listeners
  useEffect(() => {
    if (!isActiveInput) return

    const recognition = SpeechRecognition.getRecognition()
    if (!recognition) return

    recognitionRef.current = recognition

    const handleError = (event: any) => {
      if (event.error === 'no-speech') {
        toast.handler({
          content:
            'Nenhuma fala detectada. Verifique se o microfone está funcionando e tente falar mais alto.',
          severity: 'warning'
        })
      } else if (event.error === 'not-allowed') {
        toast.handler({
          content:
            'Permissão para usar o microfone foi negada. Verifique as configurações do navegador.',
          severity: 'error'
        })
      } else if (event.error === 'audio-capture') {
        toast.handler({
          content:
            'Erro ao acessar o microfone. Verifique se está conectado e funcionando.',
          severity: 'error'
        })
      }
    }

    recognition.addEventListener('error', handleError)

    return () => {
      recognition.removeEventListener('error', handleError)
    }
  }, [isActiveInput, componentId, toast])

  // Pre-warm: initialize once per page session if mic permission already granted
  useEffect(() => {
    if (!browserSupportsSpeechRecognition) return

    const runWarmup = async () => {
      try {
        const status = await navigator.permissions.query({
          name: 'microphone' as PermissionName
        })
        if (status.state === 'granted' && !warmupDone) {
          warmupDone = true
          await SpeechRecognition.startListening({ continuous: false })
          SpeechRecognition.abortListening()
        }
      } catch {
        // Permissions API not available or warmup failed — silent
      }
    }

    runWarmup()
  }, [browserSupportsSpeechRecognition])

  // Handle microphone click for this specific component
  const handleMicClick = async () => {
    if (isActiveInput) {
      SpeechRecognition.stopListening()
      setIsActiveInput(false)
      hasStartedListeningRef.current = false
    } else if (!isStarting) {
      setIsStarting(true)
      setBaseText(value)
      setIsActiveInput(true)
      resetTranscript()

      const recognition = SpeechRecognition.getRecognition()
      if (recognition) {
        recognition.addEventListener(
          'audiostart',
          () => {
            setIsStarting(false)
            toast.handler({ content: 'Pode falar agora!', severity: 'info' })
          },
          { once: true }
        )
      }

      hasStartedListeningRef.current = true

      SpeechRecognition.startListening({ continuous: true, language: 'pt-BR' })
        .catch(() =>
          SpeechRecognition.startListening({
            continuous: true,
            language: 'pt-PT'
          }).catch(() => SpeechRecognition.startListening({ continuous: true }))
        )
        .catch(() => {
          setIsStarting(false)
          hasStartedListeningRef.current = false
        })
    }
  }

  // Update in real-time only if THIS input is active
  useEffect(() => {
    if (isActiveInput && listening && transcript) {
      const newText = baseText + (baseText ? ' ' : '') + transcript
      const syntheticEvent = {
        target: { value: newText }
      } as React.ChangeEvent<HTMLInputElement>
      onChange(syntheticEvent)
    }
  }, [transcript, isActiveInput, listening])

  // Deactivate this input when listening stops
  useEffect(() => {
    if (!listening && isActiveInput && hasStartedListeningRef.current) {
      setIsActiveInput(false)
      hasStartedListeningRef.current = false
    }
  }, [listening])

  const getMicIcon = () => {
    if (isStarting)
      return <CircularProgress size={20} sx={{ color: 'inherit' }} />
    if (isActiveInput) return <StopCircle />
    return <Mic />
  }

  const getTooltipTitle = () => {
    if (!browserSupportsSpeechRecognition) {
      return 'Seu navegador não suporta reconhecimento de voz'
    }
    if (isStarting) {
      return 'Preparando gravação... Aguarde'
    }
    if (isActiveInput) {
      return 'Parar captura de voz'
    }
    return 'Iniciar captura de voz'
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        fullWidth
        multiline
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        label={label}
        variant='outlined'
        disabled={disabled}
        inputRef={inputRef}
      />

      {/* Botão de captura de voz destacado */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <Tooltip title={getTooltipTitle()}>
          <span>
            <Button
              variant='outlined'
              size='medium'
              startIcon={getMicIcon()}
              disabled={
                !browserSupportsSpeechRecognition || disabled || isStarting
              }
              onClick={handleMicClick}
              sx={{
                borderRadius: 2,
                px: 2,
                textTransform: 'none',
                borderColor: isActiveInput ? 'error.main' : 'primary.main',
                color: isActiveInput ? 'error.main' : 'primary.main',
                '&:hover': {
                  borderColor: isActiveInput ? 'error.dark' : 'primary.dark',
                  backgroundColor: isActiveInput
                    ? 'rgba(211, 47, 47, 0.04)'
                    : 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              {isActiveInput
                ? 'Parar captura de voz'
                : 'Iniciar captura de voz'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default TranscriptionInput
